import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import type * as Flows from './types.no-deps'

export function hasShowIfs(
    element: Flows.Element | Flows.NestedElement
): element is
    | Flows.CalculationElement
    | Flows.CheckboxElement
    | Flows.ConfirmationElement
    | Flows.DropdownElement
    | Flows.ImageSelectElement
    | Flows.NumberElement
    | Flows.RadioElement
    | Flows.TextCaptureElement
    | Flows.YesNoElement {
    return element.hasOwnProperty('showIfs')
}

export function hasRequired(
    element: Flows.Section | Flows.Element | Flows.NestedElement
): element is
    | Flows.CheckboxElement
    | Flows.ConfirmationElement
    | Flows.DateTimeElement
    | Flows.DropdownElement
    | Flows.ImageSelectElement
    | Flows.NumberElement
    | Flows.CameraElement
    | Flows.RadioElement
    | Flows.SectionElement
    | Flows.SignatureElement
    | Flows.TextCaptureElement
    | Flows.UnitSelectElement
    | Flows.YesNoElement
    | Flows.NestedCheckboxElement
    | Flows.NestedConfirmationElement
    | Flows.NestedDateTimeElement
    | Flows.NestedDropdownElement
    | Flows.NestedImageSelectElement
    | Flows.NestedNumberElement
    | Flows.NestedCameraElement
    | Flows.NestedRadioElement
    | Flows.NestedSignatureElement
    | Flows.NestedTextCaptureElement
    | Flows.NestedUnitSelectElement
    | Flows.NestedYesNoElement
    | Flows.NestedSubtaskElement
    | Flows.NestedGuideElement {
    switch (element.type) {
        case 'checkbox':
        case 'confirmation':
        case 'datetime':
        case 'dropdown':
        case 'image-select':
        case 'number':
        case 'photo':
        case 'radio':
        case 'signature':
        case 'section':
        case 'text-capture':
        case 'yes-no':
        case 'files-select':
        case 'unit-select':
        case 'flow':
        case 'guide':
            return true
        case 'calculation':
        case 'image-content':
        case 'text-content':
        case 'video-content':
        case 'link':
        case 'loop':
            return false
        default:
            assumeExhaustion(element)
            return false
    }
}

export function hasInputs(
    element: Flows.Element | Flows.NestedElement | Flows.Section
): element is
    | Flows.CheckboxElement
    | Flows.ConfirmationElement
    | Flows.DateTimeElement
    | Flows.DropdownElement
    | Flows.ImageSelectElement
    | Flows.NumberElement
    | Flows.CameraElement
    | Flows.RadioElement
    | Flows.SectionElement
    | Flows.SignatureElement
    | Flows.TextCaptureElement
    | Flows.UnitSelectElement
    | Flows.YesNoElement
    | Flows.FilesSelectElement
    | Flows.NestedCheckboxElement
    | Flows.NestedConfirmationElement
    | Flows.NestedDateTimeElement
    | Flows.NestedDropdownElement
    | Flows.NestedImageSelectElement
    | Flows.NestedNumberElement
    | Flows.NestedCameraElement
    | Flows.NestedRadioElement
    | Flows.NestedSignatureElement
    | Flows.NestedTextCaptureElement
    | Flows.NestedUnitSelectElement
    | Flows.NestedYesNoElement
    | Flows.NestedSubtaskElement
    | Flows.NestedFilesSelectElement
    | Flows.NestedGuideElement {
    switch (element.type) {
        case 'checkbox':
        case 'confirmation':
        case 'datetime':
        case 'dropdown':
        case 'image-select':
        case 'number':
        case 'photo':
        case 'radio':
        case 'section':
        case 'signature':
        case 'text-capture':
        case 'yes-no':
        case 'files-select':
        case 'unit-select':
        case 'flow':
        case 'guide':
            return true
        case 'calculation':
        case 'image-content':
        case 'text-content':
        case 'video-content':
        case 'link':
        case 'loop':
            return false
        default:
            assumeExhaustion(element)
            return false
    }
}

export function hasLabel(section: Flows.Section | Flows.SectionElement): section is Flows.SectionElement {
    return section.hasOwnProperty('label')
}

export function hasImageSize(element: Flows.Element | Flows.NestedElement): element is Flows.ImageSelectElement {
    return element.type === 'image-select'
}

export function hasRange(
    element: Flows.Element | Flows.NestedElement
): element is Flows.NumberElement | Flows.NestedNumberElement {
    return element.type === 'number'
}

export function hasUnitOfMeasurement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.NumberElement | Flows.NestedNumberElement {
    return element.type === 'number'
}

export function isNumberElement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.NumberElement | Flows.NestedNumberElement {
    return element.type === 'number'
}

export function hasChoices(
    element: Flows.Element | Flows.NestedElement
): element is
    | Flows.CheckboxElement
    | Flows.RadioElement
    | Flows.ImageSelectElement
    | Flows.DropdownElement
    | Flows.NestedCheckboxElement
    | Flows.NestedRadioElement
    | Flows.NestedImageSelectElement
    | Flows.NestedDropdownElement {
    switch (element.type) {
        case 'checkbox':
        case 'radio':
        case 'dropdown':
        case 'image-select':
            return true
        case 'calculation':
        case 'confirmation':
        case 'datetime':
        case 'image-content':
        case 'number':
        case 'photo':
        case 'section':
        case 'signature':
        case 'text-capture':
        case 'text-content':
        case 'yes-no':
        case 'files-select':
        case 'unit-select':
        case 'video-content':
        case 'link':
        case 'flow':
        case 'loop':
        case 'guide':
            return false
        default:
            assumeExhaustion(element)
            return false
    }
}

export function answerHasChoices(
    answer: Flows.Answer
): answer is Flows.CheckboxAnswer | Flows.ImageSelectAnswer | Flows.DropdownAnswer {
    switch (answer.type) {
        case 'checkbox':
        case 'dropdown':
        case 'image-select':
            return true
        case 'confirmation':
        case 'datetime':
        case 'number':
        case 'photo':
        case 'section':
        case 'signature':
        case 'text-capture':
        case 'yes-no':
        case 'radio':
        case 'files-select':
        case 'extensible-files':
        case 'unit-select':
        case 'reports':
        case 'guide':
            return false
        default:
            assumeExhaustion(answer)
            return false
    }
}

export function hasImageChoices(
    element: Flows.Element | Flows.NestedElement
): element is Flows.ImageSelectElement | Flows.NestedImageSelectElement {
    return element.type === 'image-select'
}

export function hasDropdownChoices(
    element: Flows.Element | Flows.NestedElement
): element is Flows.DropdownElement | Flows.NestedDropdownElement {
    return element.type === 'dropdown'
}

export function hasChoiceRange(
    element: Flows.Element | Flows.NestedElement
): element is
    | Flows.CheckboxElement
    | Flows.ImageSelectElement
    | Flows.DropdownElement
    | Flows.NestedCheckboxElement
    | Flows.NestedImageSelectElement
    | Flows.NestedDropdownElement {
    switch (element.type) {
        case 'checkbox':
        case 'dropdown':
        case 'image-select':
            return true
        case 'calculation':
        case 'confirmation':
        case 'datetime':
        case 'image-content':
        case 'number':
        case 'photo':
        case 'radio':
        case 'section':
        case 'signature':
        case 'text-capture':
        case 'text-content':
        case 'yes-no':
        case 'files-select':
        case 'unit-select':
        case 'video-content':
        case 'link':
        case 'flow':
        case 'loop':
        case 'guide':
            return false
        default:
            assumeExhaustion(element)
            return false
    }
}

export function hasPhotoRange(
    element: Flows.Element | Flows.NestedElement
): element is Flows.CameraElement | Flows.NestedCameraElement {
    return element.type === 'photo'
}

export function hasFormula(
    element: Flows.Element | Flows.NestedElement
): element is Flows.CalculationElement | Flows.NestedCalculationElement {
    return element.type === 'calculation'
}

export function hasTextRange(
    element: Flows.Element | Flows.NestedElement
): element is Flows.TextCaptureElement | Flows.NestedTextCaptureElement {
    return element.type === 'text-capture'
}

export function hasMultipleLines(
    element: Flows.Element | Flows.NestedElement
): element is Flows.TextCaptureElement | Flows.NestedTextCaptureElement {
    return element.type === 'text-capture'
}

export function hasURL(
    element: Flows.Element | Flows.NestedElement
): element is
    | Flows.ImageContentElement
    | Flows.NestedImageContentElement
    | Flows.VideoContentElement
    | Flows.NestedVideoContentElement {
    return element.type === 'image-content' || element.type === 'video-content'
}

export function hasHTML(
    element: Flows.Element | Flows.NestedElement
): element is Flows.TextContentElement | Flows.NestedTextContentElement {
    return element.type === 'text-content'
}

export function hasRichText(
    element: Flows.Element | Flows.NestedElement
): element is Flows.RichTextContentElement | Flows.NestedTextContentElement {
    return element.type === 'text-content'
}

export function hasDateTimeFields(
    element: Flows.Element | Flows.NestedElement
): element is Flows.DateTimeElement | Flows.NestedDateTimeElement {
    return element.type === 'datetime'
}

export function isImageContentElement<T>(
    element: T & Flows.NestedElement
): element is T & Flows.NestedImageContentElement {
    return element.type === 'image-content'
}

export function isImageSelectElement<T>(
    element: T & Flows.NestedElement
): element is T & Flows.NestedImageSelectElement {
    return element.type === 'image-select'
}

export function isUnitSelectElement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.UnitSelectElement | Flows.NestedUnitSelectElement {
    return element.type === 'unit-select'
}

export function isSectionElement(element: Flows.Element | Flows.NestedElement): element is Flows.SectionElement {
    return element.type === 'section'
}

export function isSubtaskElement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.SubtaskElement | Flows.NestedSubtaskElement {
    return element.type === 'flow'
}

export function isSignatureElement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.SignatureElement | Flows.NestedSignatureElement {
    return element.type === 'signature'
}

export function isTextCaptureElement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.TextCaptureElement | Flows.NestedTextCaptureElement {
    return element.type === 'text-capture'
}

export function isFileSelectElement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.FilesSelectElement | Flows.NestedFilesSelectElement {
    return element.type === 'files-select'
}

export function isCameraElement(
    element: Flows.Element | Flows.NestedElement
): element is Flows.CameraElement | Flows.NestedCameraElement {
    return element.type === 'photo'
}

export function isLoopElement(element: Flows.Element | Flows.NestedElement): element is Flows.LoopElement {
    return element.type === 'loop'
}

export function isGuideElement(element: Flows.Element | Flows.NestedElement): element is Flows.GuideElement {
    return element.type === 'guide'
}

export function isGuideAnswer(answer: Flows.Answer): answer is Flows.GuideAnswer {
    return answer.type === 'guide'
}
